import React, { useEffect, useState, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import smallLogo from "../../../assets/images/inc-logo-small.png";
import fullLogo from "../../../assets/images/inc-name-logo.png";
import vvdnLogo from "../../../assets/images/VVDN.png";
import cpPlusLogo from "../../../assets/images/CPPlus.png";
import iceLogo from "../../../assets/images/ICE.png";
import { jwtDecode } from "jwt-decode";
import { useLocalization } from "../localization/LocalizationContext.js";
import CustomHierarchyDropdown from "./HierarchyTree.js";
import { useNetwork } from "../../component/common/networkContext";
import { useSidebar } from "../common/sidebarContext.js";

const Sidebar = () => {
  const [organization, setOrganization] = useState("");
  const [license, setLicense] = useState("");
  const [activeMenu, setActiveMenu] = useState(null);
  const { isCollapsed, toggleSidebar, isMobileNavOpen, closeMobileNav } = useSidebar();
  const location = useLocation();
  const compactNav = isCollapsed && !isMobileNavOpen;
  const { localizationStrings } = useLocalization();
  //const { setSelectedNetworkId, setSelectedLocation } = useNetwork();

  const treeData = [
    { id: "loc1", label: "Manesar", type: "Loc" },
    { id: "loc2", label: "Pollachi", type: "Loc" },
    { id: "loc3", label: "Gurgaon", type: "Loc" },
    { id: "loc4", label: "Noida", type: "Loc" },
    { id: "loc5", label: "Pune", type: "Loc" },
  ];

  const dropdownMenus = ["device", "model", "network"];

  useEffect(() => {
    closeMobileNav();
  }, [location.pathname, closeMobileNav]);

  const decodeOrganization = () => {
    let jwtToken = localStorage.getItem("sessionToken");
    if (jwtToken) {
      try {
        let decodedToken = jwtDecode(jwtToken);
        setOrganization(decodedToken?.organization);
      } catch (error) {
        console.error("Failed to decode token:", error);
        setOrganization("");
      }
    }
  };

  const fetchLicense = () => {
    const storedLicense = localStorage.getItem("license");
    if (storedLicense) {
      setLicense(storedLicense);
    }
  };

  useEffect(() => {
    decodeOrganization();
    fetchLicense();
  }, []);

  const toggleDropdown = (menuName) => {
    setActiveMenu((prev) => (prev === menuName ? null : menuName));
  };

  // collapsed hierarchy popup state
  const [showHierarchyPopup, setShowHierarchyPopup] = useState(false);
  const popupRef = useRef(null);
  const hierarchyIconRef = useRef(null);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const [collapsedSubmenu, setCollapsedSubmenu] = useState(null);
  const collapsedSubmenuRef = useRef(null);

  const handleCollapsedHierarchyClick = () => {
    if (!showHierarchyPopup && hierarchyIconRef.current) {
      const rect = hierarchyIconRef.current.getBoundingClientRect();
      setPopupPosition({ top: rect.top, left: rect.right + 10 });
    }
    setShowHierarchyPopup((s) => !s);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!showHierarchyPopup) return;
      if (
        popupRef.current &&
        !popupRef.current.contains(e.target) &&
        !e.target.closest('.navbar-hierarchy-box')
      ) {
        setShowHierarchyPopup(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showHierarchyPopup]);

  useEffect(() => {
    const handleCollapsedSubmenuOutsideClick = (event) => {
      if (
        collapsedSubmenu &&
        collapsedSubmenuRef.current &&
        !collapsedSubmenuRef.current.contains(event.target) &&
        !event.target.closest(".collapsed-dropdown-trigger")
      ) {
        setCollapsedSubmenu(null);
      }
    };

    document.addEventListener("mousedown", handleCollapsedSubmenuOutsideClick);
    return () => document.removeEventListener("mousedown", handleCollapsedSubmenuOutsideClick);
  }, [collapsedSubmenu]);

  useEffect(() => {
    if (!compactNav) setCollapsedSubmenu(null);
  }, [compactNav]);

  const handleCollapsedDropdownClick = (event, menuName) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setCollapsedSubmenu((current) =>
      current?.name === menuName
        ? null
        : { name: menuName, top: rect.top, left: rect.right + 8 }
    );
  };

  const closeCollapsedSubmenu = () => setCollapsedSubmenu(null);

  const renderCollapsedSubmenu = () => {
    if (!compactNav || !collapsedSubmenu) return null;

    const submenuItems = {
      device: [
        { label: localizationStrings?.devices, to: "/device-listing" },
      ],
      model: [
        { label: "SNMP", to: "/modal-management/create-template" },
        { label: "HTTPS", to: "/modal-management/https" },
      ],
      network: [
        { label: localizationStrings?.wiredDevices, to: "/network-management/wired-config" },
        { label: localizationStrings?.wirelessDevices, to: "/network-management/wifi-config" },
      ],
    };

    return (
      <div
        ref={collapsedSubmenuRef}
        className="collapsed-sidebar-submenu"
        style={{ top: collapsedSubmenu.top, left: collapsedSubmenu.left }}
      >
        <div className="collapsed-sidebar-submenu-title">
          {collapsedSubmenu.name === "device" && localizationStrings?.deviceManagement}
          {collapsedSubmenu.name === "model" && localizationStrings?.modelManagement}
          {collapsedSubmenu.name === "network" && localizationStrings?.networkManagement}
        </div>
        {submenuItems[collapsedSubmenu.name].map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className="collapsed-sidebar-submenu-link"
            onClick={() => {
              setActiveMenu(collapsedSubmenu.name);
              closeCollapsedSubmenu();
            }}
          >
            {item.label}
          </NavLink>
        ))}
      </div>
    );
  };

  return (
    <div className={`navbar-menu ${compactNav ? "collapsed" : ""} ${isMobileNavOpen ? "mobile-open" : ""}`}>
      <div className="navbar-brand-box">
        <img
          src={
            compactNav
              ? smallLogo
              : organization === "VVDN Technologies"
                ? vvdnLogo
                : organization === "CP PLUS INDIA"
                  ? cpPlusLogo
                  : organization === "Intelligent Cloud"
                    ? iceLogo
                    : fullLogo
          }
          alt="INC"
          className={compactNav ? "collapsed-logo" : "full-logo"}
        />
      </div>
      {compactNav ? (
        <>
          <div
            className="navbar-hierarchy-box collapsed-icon"
            title="Hierarchy Tree"
            onClick={handleCollapsedHierarchyClick}
            ref={hierarchyIconRef}
            style={{ cursor: 'pointer' }}
          >
            <i className="fa fa-sitemap" aria-hidden="true"></i>
          </div>
          {showHierarchyPopup && (
            <div
              ref={popupRef}
              className="collapsed-hierarchy-popup"
              style={{ top: popupPosition.top, left: popupPosition.left }}
            >
              <CustomHierarchyDropdown
                initialOpen={true}
                popupMode={true}
                onSelect={(node) => {
                  console.log("Selected node:", node);
                  setShowHierarchyPopup(false);
                }}
              />
            </div>
          )}
        </>
      ) : (
        <div className="navbar-hierarchy-box">
          <CustomHierarchyDropdown onSelect={(node) => { console.log("Selected node:", node); }} />
        </div>
      )}

      <div className="simplebar-scrollable-y">
        <nav className="inc-navigation">
          <ul>
            <li>
              <NavLink
                to="/overview"
                className={({ isActive }) =>
                  isActive && !dropdownMenus.includes(activeMenu)
                    ? "active"
                    : ""
                }
                onClick={() => setActiveMenu("overview")}
              >
                <i className="dashboard icon"></i>
                <span className="nav-label">{localizationStrings?.overview}</span>
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/floormap"
                className={({ isActive }) =>
                  isActive && !dropdownMenus.includes(activeMenu)
                    ? "active"
                    : ""
                }
                onClick={() => setActiveMenu("floormap")}
              >
                <i className="floormap icon"></i>
                <span className="nav-label">{localizationStrings?.floorMap}</span>
              </NavLink>
            </li>

            {/* {license !== "Standard" && (
              <li>
                <NavLink
                  to="/tenant-management"
                  className={({ isActive }) =>
                    isActive && !dropdownMenus.includes(activeMenu)
                      ? "active"
                      : ""
                  }
                  onClick={() => setActiveMenu("tenant-management")}
                >
                  <i className="account_circle icon"></i>{" "}
                  <span className="nav-label">{localizationStrings?.tenantManagement}</span>
                </NavLink>
              </li>
            )} */}

            <li>
              <NavLink
                to="/user-management"
                className={({ isActive }) =>
                  isActive && !dropdownMenus.includes(activeMenu)
                    ? "active"
                    : ""
                }
                onClick={() => setActiveMenu("user-management")}
              >
                <i className="supervisor_account icon"></i>{" "}
                <span className="nav-label">{localizationStrings?.userManagement}</span>
              </NavLink>
            </li>

            {/* Device Dropdown */}
            <li
              className={`dropdown ${activeMenu === "device" ? "open active" : ""
                }`}
            >
              <button
                className={compactNav ? "collapsed-dropdown-trigger" : ""}
                onClick={(event) =>
                  compactNav
                    ? handleCollapsedDropdownClick(event, "device")
                    : toggleDropdown("device")
                }
              >
                <i className="router icon"></i>{" "}
                <span className="nav-label">{localizationStrings?.deviceManagement}</span>
                <i
                  className={`fa ${activeMenu === "device" ? "fa-angle-up" : "fa-angle-down"
                    }`}
                ></i>
              </button>
              {activeMenu === "device" && (
                <ul className="dropdown-menu">
                  <li>
                    <NavLink
                      to="/device-listing"
                      onClick={() => setActiveMenu("device")}
                    >
                      {localizationStrings?.devices}
                    </NavLink>
                  </li>
                </ul>
              )}
            </li>

            {/* Model Dropdown */}
            <li
              className={`dropdown ${activeMenu === "model" ? "open active" : ""
                }`}
            >
              <button
                className={compactNav ? "collapsed-dropdown-trigger" : ""}
                onClick={(event) =>
                  compactNav
                    ? handleCollapsedDropdownClick(event, "model")
                    : toggleDropdown("model")
                }
              >
                <i className="alpha-d-box icon"></i>{" "}
                <span className="nav-label">{localizationStrings?.modelManagement}</span>
                <i
                  className={`fa ${activeMenu === "model" ? "fa-angle-up" : "fa-angle-down"
                    }`}
                ></i>
              </button>
              {activeMenu === "model" && (
                <ul className="dropdown-menu">
                  <li>
                    <NavLink
                      to="/modal-management/create-template"
                      onClick={() => setActiveMenu("model")}
                    >
                      SNMP
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/modal-management/https"
                      onClick={() => setActiveMenu("model")}
                    >
                      HTTPS
                    </NavLink>
                  </li>
                </ul>
              )}
            </li>

            <li>
              <NavLink
                to="/edge-management"
                className={({ isActive }) =>
                  isActive && !dropdownMenus.includes(activeMenu)
                    ? "active"
                    : ""
                }
                onClick={() => setActiveMenu("edge-management")}
              >
                <i className="devices_other icon"></i>{" "}
                <span className="nav-label">{localizationStrings?.edgeManagement}</span>
              </NavLink>
            </li>

            {/* Network Dropdown */}
            <li
              className={`dropdown ${activeMenu === "network" ? "open active" : ""
                }`}
            >
              <button
                className={compactNav ? "collapsed-dropdown-trigger" : ""}
                onClick={(event) =>
                  compactNav
                    ? handleCollapsedDropdownClick(event, "network")
                    : toggleDropdown("network")
                }
              >
                <i className="alpha-d-box icon"></i>{" "}
                <span className="nav-label">{localizationStrings?.networkManagement}</span>
                <i
                  className={`fa ${activeMenu === "network" ? "fa-angle-up" : "fa-angle-down"
                    }`}
                ></i>
              </button>
              {activeMenu === "network" && (
                <ul className="dropdown-menu">
                  <li>
                    <NavLink
                      to="/network-management/wired-config"
                      onClick={() => setActiveMenu("network")}
                    >
                      {localizationStrings?.wiredDevices}
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/network-management/wifi-config"
                      onClick={() => setActiveMenu("network")}
                    >
                      {localizationStrings?.wirelessDevices}
                    </NavLink>
                  </li>
                </ul>
              )}
            </li>

            <li>
              <NavLink
                to="/wired"
                className={({ isActive }) =>
                  isActive && !dropdownMenus.includes(activeMenu)
                    ? "active"
                    : ""
                }
                onClick={() => setActiveMenu("wired")}
              >
                <i className="wired icon"></i>{" "}
                <span className="nav-label">{localizationStrings?.wiredDashboard}</span>
              </NavLink>
            </li>

            <li>
              <NavLink
                to="/wireless"
                className={({ isActive }) =>
                  isActive && !dropdownMenus.includes(activeMenu)
                    ? "active"
                    : ""
                }
                onClick={() => setActiveMenu("wireless")}
              >
                <i className="wireless icon"></i>{" "}
                <span className="nav-label">{localizationStrings?.wirelessDashboard}</span>
              </NavLink>
            </li>

            <li>
              <NavLink
                to="/client"
                className={({ isActive }) =>
                  isActive && !dropdownMenus.includes(activeMenu)
                    ? "active"
                    : ""
                }
                onClick={() => setActiveMenu("client")}
              >
                <i className="client icon"></i>
                <span className="nav-label">{localizationStrings?.client}</span>
              </NavLink>
            </li>

            <li>
              <NavLink
                to="/troubleshoot"
                className={({ isActive }) =>
                  isActive && !dropdownMenus.includes(activeMenu)
                    ? "active"
                    : ""
                }
                onClick={() => setActiveMenu("troubleshoot")}
              >
                <i className="troubleshoot icon"></i>{" "}
                <span className="nav-label">{localizationStrings?.troubleshoot}</span>
              </NavLink>
            </li>

            {license !== "Standard" && (
              <>
                <li>
                  <NavLink
                    to="/alarms"
                    className={({ isActive }) =>
                      isActive && !dropdownMenus.includes(activeMenu)
                        ? "active"
                        : ""
                    }
                    onClick={() => setActiveMenu("alarms")}
                  >
                    <i className="fault icon"></i>
                    <span className="nav-label">{localizationStrings?.alerts}</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/topology"
                    className={({ isActive }) =>
                      isActive && !dropdownMenus.includes(activeMenu)
                        ? "active"
                        : ""
                    }
                    onClick={() => setActiveMenu("topology")}
                  >
                    <i className="domain icon"></i>{" "}
                    <span className="nav-label">{localizationStrings?.topology}</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/OTAManagement"
                    className={({ isActive }) =>
                      isActive && !dropdownMenus.includes(activeMenu)
                        ? "active"
                        : ""
                    }
                    onClick={() => setActiveMenu("OTAManagement")}
                  >
                    <i className="memory icon"></i>{" "}
                    <span className="nav-label">{localizationStrings?.otaManagement}</span>
                  </NavLink>
                </li>
                {/* <li>
                  <NavLink
                    to="/allReports"
                    className={({ isActive }) =>
                      isActive && !dropdownMenus.includes(activeMenu)
                        ? "active"
                        : ""
                    }
                    onClick={() => setActiveMenu("allReports")}
                  >
                    <i className="report icon"></i>{" "}
                    <span className="nav-label">{localizationStrings?.reports}</span>
                  </NavLink>
                </li> */}
                <li>
                  <NavLink
                    to="/audits"
                    className={({ isActive }) =>
                      isActive && !dropdownMenus.includes(activeMenu)
                        ? "active"
                        : ""
                    }
                    onClick={() => setActiveMenu("audits")}
                  >
                    <i className="icon track_changes"></i>{" "}
                    <span className="nav-label">{localizationStrings?.audits}</span>
                  </NavLink>
                </li>

                {license !== "Business" && (
                  <li>
                    <NavLink
                      to="/system-info"
                      className={({ isActive }) =>
                        isActive && !dropdownMenus.includes(activeMenu)
                          ? "active"
                          : ""
                      }
                      onClick={() => setActiveMenu("system-info")}
                    >
                      <i className="settings icon"></i>{" "}
                      <span className="nav-label">{localizationStrings?.systemInformation}</span>
                    </NavLink>
                  </li>
                )}
                {license !== "Business" && (
                  <li>
                    <NavLink
                      to="/anomalies"
                      className={({ isActive }) =>
                        isActive && !dropdownMenus.includes(activeMenu)
                          ? "active"
                          : ""
                      }
                      onClick={() => setActiveMenu("anomalies")}
                    >
                      <i className="troubleshoot icon"></i>{" "}
                      <span className="nav-label">{localizationStrings?.anomalies}</span>
                    </NavLink>
                  </li>
                )}
                <li>
                  {/* privacy policy link intentionally hidden */}
                </li>
              </>
            )}
          </ul>
        </nav>
      </div>
      {renderCollapsedSubmenu()}
      <div className="sidebar-toggle-box">
        <button className="sidebar-toggle-btn" onClick={toggleSidebar} title={isCollapsed ? "Expand" : "Collapse"}>
          <span className="sidebar-toggle-icon">
            <i className={`fa ${isCollapsed ? "fa-angle-right" : "fa-angle-left"}`} aria-hidden="true" />
          </span>
        </button>
      </div>
    </div>
  );
};

export default Sidebar;